import React, { useState, useEffect } from "react";
import Select, { components } from "react-select";
import {
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Highlighter from "react-highlight-words";
import { FixedSizeList as List } from "react-window";

const ITEM_HEIGHT = 50;
const MAX_MENU_HEIGHT = 340;

const MenuList = (props) => {
  const { children, maxHeight, getValue, isLoading, selectProps } = props;

  const isMobile = useMediaQuery("(max-width:600px)");
  const { isVisualization } = selectProps;

  const childOptions = React.Children.toArray(children);
  const [selectedOption] = getValue();

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 10 }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (isMobile && !isVisualization) {
    return <components.MenuList {...props}>{children}</components.MenuList>;
  }

  if (childOptions.length === 0) {
    return <components.MenuList {...props} />;
  }

  const selectedIndex = childOptions.findIndex(
    (child) => child.props?.data?.value === selectedOption?.value,
  );

  const availableHeight = Math.min(
    maxHeight || MAX_MENU_HEIGHT,
    MAX_MENU_HEIGHT,
  );

  const listHeight = Math.min(
    childOptions.length * ITEM_HEIGHT,
    availableHeight,
  );

  const MenuRow = ({ index, style }) => (
    <div style={style}>{childOptions[index]}</div>
  );

  return (
    <List
      height={listHeight}
      itemCount={childOptions.length}
      itemSize={ITEM_HEIGHT}
      width="100%"
      initialScrollOffset={Math.max(selectedIndex, 0) * ITEM_HEIGHT}
      overscanCount={5}
    >
      {MenuRow}
    </List>
  );
};

const Option = (props) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const { label } = props.data;
  const { inputValue, isHighlighter, highlightStyle } = props.selectProps;

  if (typeof label !== "string") {
    return <components.Option {...props} />;
  }

  const formatLabel = (labelText) => (
    <Highlighter
      searchWords={inputValue ? [inputValue] : []}
      textToHighlight={labelText}
      highlightStyle={
        isHighlighter
          ? {
              fontWeight: "bold",
              padding: 0,
              backgroundColor: "transparent",
              ...highlightStyle,
            }
          : {}
      }
    />
  );

  if (isMobile) {
    const parts = label.split("/");

    return (
      <components.Option {...props}>
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span style={{ color: "#999" }}>/</span>}
              <span style={{ display: "inline-block" }}>
                {isHighlighter ? formatLabel(part) : part}
              </span>
            </React.Fragment>
          ))}
        </div>
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      {isHighlighter ? formatLabel(label) : label}
    </components.Option>
  );
};

const RCSelect = ({
  label,
  value,
  error,
  options,
  onChange,
  isLoading = false,
  isDisabled = false,
  styles = {},
  placeholder,
  isHighlighter = true,
  highlightStyle,
  isClearable = true,
  isSearchable = true,
  isVisualization = false,
  menuPlacement = "bottom",
  maxMenuHeight = MAX_MENU_HEIGHT,
  menuPosition = "fixed",
  menuShouldScrollIntoView = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 50,
      height: 50,
      width: "100%",
      borderRadius: 8,
      borderColor: state.isFocused ? theme.palette.primary.main : "#ccc",
      boxShadow: state.isFocused
        ? `0 0 0 2px ${theme.palette.primary.main}33`
        : "none",
      "&:hover": {
        borderColor: theme.palette.primary.main,
      },
    }),
    container: (provided) => ({
      ...provided,
      width: "100%",
      minWidth: 0,
    }),
    option: (provided, state) => ({
      ...provided,
      boxSizing: "border-box",
      minHeight: ITEM_HEIGHT,
      padding: "10px 12px",
      backgroundColor: state.isSelected
        ? theme.palette.primary.main
        : state.isFocused
          ? `${theme.palette.primary.main}22`
          : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      cursor: "pointer",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: 50,
      padding: "0 8px",
      minWidth: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: 50,
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 6,
      marginBottom: 6,
      zIndex: 1600,
      backgroundColor: "#FFFFFF",
      border: "1px solid #B8C5CC",
      borderRadius: 10,
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.22)",
      overflow: "hidden",
    }),

    menuList: (provided) => ({
      ...provided,
      maxHeight: Math.min(maxMenuHeight, MAX_MENU_HEIGHT),
      overflowY: "auto",
      paddingTop: 4,
      paddingBottom: 4,
      backgroundColor: "#FFFFFF",
    }),

    option: (provided, state) => ({
      ...provided,
      boxSizing: "border-box",
      minHeight: ITEM_HEIGHT,
      padding: "14px 12px",
      backgroundColor: state.isSelected
        ? "#D0F3F0"
        : state.isFocused
          ? "#E8F7F6"
          : "#FFFFFF",
      color: "#263238",
      borderBottom: "1px solid #F0F2F4",
      cursor: "pointer",
      ":active": {
        backgroundColor: "#C5EEEA",
      },
    }),

    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1600,
    }),
  };

  return (
    <>
      {label && (
        <Typography variant="body2" sx={{ mb: "4px" }}>
          {label}
        </Typography>
      )}

      <Select
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        options={options}
        value={value}
        onChange={onChange}
        isLoading={isLoading}
        isDisabled={isDisabled}
        styles={{
          ...customStyles,
          ...styles,
        }}
        components={{
          MenuList,
          Option,
        }}
        isHighlighter={isHighlighter}
        highlightStyle={highlightStyle}
        isVisualization={isVisualization}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition={menuPosition}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        menuShouldScrollIntoView={menuShouldScrollIntoView}
        classNamePrefix="rc-select"
      />

      {error && (
        <Typography variant="body2" color="error" sx={{ my: 2 }}>
          {error}
        </Typography>
      )}
    </>
  );
};

export default RCSelect;
